import React, { useState, useEffect, useRef } from 'react';
import { 
  Upload, 
  FileSpreadsheet, 
  FileText, 
  File,
  Download, 
  Trash2, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  ArrowLeft,
  Database,
  Clock,
  HardDrive,
  Settings,
  ChevronDown,
  Loader2,
  Eye,
  FileIcon,
  FolderOpen,
  MessageSquare,
  Sparkles,
  Lock,
  Unlock,
  RotateCcw,
  Save,
  Languages,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Document categories
const DOC_CATEGORIES = {
  general: { name: 'Geral', icon: '📄' },
  financial: { name: 'Financeiro', icon: '💼' },
  operational: { name: 'Operacional', icon: '⚙️' },
  hr: { name: 'Recursos Humanos', icon: '👥' },
  esg: { name: 'ESG', icon: '🌱' },
};

// Data type configurations
const DATA_TYPES = {
  revenue: { 
    name: 'Receita', 
    color: 'emerald',
    icon: '💰',
    description: 'Dados de receita por período, tipo e geografia'
  },
  ebitda: { 
    name: 'EBITDA', 
    color: 'blue',
    icon: '📊',
    description: 'EBITDA, margens e decomposição waterfall'
  },
  costs: { 
    name: 'Custos', 
    color: 'amber',
    icon: '📉',
    description: 'CMVMC, Pessoal, Gerais e Administrativos, Custos de Comercialização, Subcontratos'
  },
  production: { 
    name: 'Produção', 
    color: 'purple',
    icon: '⚙️',
    description: 'Minério processado, recuperação, quilates'
  },
  headcount: { 
    name: 'RH', 
    color: 'pink',
    icon: '👥',
    description: 'Efectivos, rotatividade, custos com pessoal'
  },
  capex: { 
    name: 'CAPEX', 
    color: 'orange',
    icon: '🏗️',
    description: 'Investimentos, execução orçamental'
  },
  cashflow: { 
    name: 'Caixa', 
    color: 'cyan',
    icon: '💵',
    description: 'Liquidez, Fluxo de Caixa Livre, dívida'
  },
};

// ============ Executive Messages Helper Components ============

const MessageFieldEditor = ({ label, description, fieldConfig, language, onUpdate, textKey }) => {
  const textField = `${textKey}_${language}`;
  const currentMode = fieldConfig?.mode || 'auto';
  const currentText = fieldConfig?.[textField] || '';
  const isLocked = fieldConfig?.is_locked || false;

  return (
    <div className="border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h5 className="font-medium text-slate-700">{label}</h5>
          <p className="text-xs text-slate-500">{description}</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={currentMode}
            onChange={(e) => onUpdate('mode', e.target.value)}
            className="text-xs border border-slate-200 rounded px-2 py-1"
            data-testid={`${label.toLowerCase().replace(' ', '-')}-mode`}
          >
            <option value="auto">🤖 Auto</option>
            <option value="manual">✏️ Manual</option>
            <option value="hybrid">🔀 Híbrido</option>
          </select>
          <button
            onClick={() => onUpdate('is_locked', !isLocked)}
            className={`p-1.5 rounded ${isLocked ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}
            title={isLocked ? 'Campo bloqueado' : 'Bloquear campo'}
          >
            {isLocked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      
      {(currentMode === 'manual' || currentMode === 'hybrid') && (
        <textarea
          value={currentText}
          onChange={(e) => onUpdate(textField, e.target.value)}
          placeholder={language === 'pt' ? 'Digite o texto personalizado...' : 'Enter custom text...'}
          className="w-full border border-slate-200 rounded-lg p-3 text-sm min-h-[80px] resize-none"
          data-testid={`${label.toLowerCase().replace(' ', '-')}-text`}
        />
      )}
      
      {currentMode === 'auto' && (
        <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-500 italic">
          Texto será gerado automaticamente com base nas métricas
        </div>
      )}
    </div>
  );
};

const MessageCardEditor = ({ cardId, config, language, onUpdate }) => {
  const cardNum = cardId.replace('card_', '');
  const titleField = `title_${language}`;
  const textField = `text_${language}`;
  const currentMode = config?.mode || 'auto';
  const currentTitle = config?.[titleField] || '';
  const currentText = config?.[textField] || '';
  const isLocked = config?.is_locked || false;

  return (
    <div className="border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 bg-[#0A192F] text-white rounded-full flex items-center justify-center text-xs font-bold">
            {cardNum}
          </span>
          <span className="font-medium text-slate-700">Cartão {cardNum}</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={currentMode}
            onChange={(e) => onUpdate('mode', e.target.value)}
            className="text-xs border border-slate-200 rounded px-2 py-1"
          >
            <option value="auto">🤖 Auto</option>
            <option value="manual">✏️ Manual</option>
            <option value="hybrid">🔀 Híbrido</option>
          </select>
          <button
            onClick={() => onUpdate('is_locked', !isLocked)}
            className={`p-1.5 rounded ${isLocked ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}
          >
            {isLocked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      
      <div className="space-y-3">
        <input
          type="text"
          value={currentTitle}
          onChange={(e) => onUpdate(titleField, e.target.value)}
          placeholder="Título do cartão"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
        />
        {(currentMode === 'manual' || currentMode === 'hybrid') && (
          <textarea
            value={currentText}
            onChange={(e) => onUpdate(textField, e.target.value)}
            placeholder={language === 'pt' ? 'Texto descritivo do cartão...' : 'Card description text...'}
            className="w-full border border-slate-200 rounded-lg p-3 text-sm min-h-[60px] resize-none"
          />
        )}
        {currentMode === 'auto' && (
          <div className="bg-slate-50 rounded-lg p-2 text-xs text-slate-500 italic">
            Texto gerado com base nas métricas
          </div>
        )}
      </div>
    </div>
  );
};

const ConclusionEditor = ({ conclusionId, config, language, onUpdate }) => {
  const concNum = conclusionId.replace('conclusion_', '');
  const titleField = `title_${language}`;
  const descField = `desc_${language}`;
  const currentMode = config?.mode || 'auto';
  const currentTitle = config?.[titleField] || '';
  const currentDesc = config?.[descField] || '';
  const isLocked = config?.is_locked || false;

  return (
    <div className="border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
            {concNum}
          </span>
          <span className="font-medium text-slate-700">Conclusão {concNum}</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={currentMode}
            onChange={(e) => onUpdate('mode', e.target.value)}
            className="text-xs border border-slate-200 rounded px-2 py-1"
          >
            <option value="auto">🤖 Auto</option>
            <option value="manual">✏️ Manual</option>
            <option value="hybrid">🔀 Híbrido</option>
          </select>
          <button
            onClick={() => onUpdate('is_locked', !isLocked)}
            className={`p-1.5 rounded ${isLocked ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}
          >
            {isLocked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      
      <div className="space-y-3">
        <input
          type="text"
          value={currentTitle}
          onChange={(e) => onUpdate(titleField, e.target.value)}
          placeholder="Título da conclusão"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-medium"
        />
        {(currentMode === 'manual' || currentMode === 'hybrid') && (
          <textarea
            value={currentDesc}
            onChange={(e) => onUpdate(descField, e.target.value)}
            placeholder={language === 'pt' ? 'Descrição da conclusão...' : 'Conclusion description...'}
            className="w-full border border-slate-200 rounded-lg p-3 text-sm min-h-[60px] resize-none"
          />
        )}
        {currentMode === 'auto' && (
          <div className="bg-slate-50 rounded-lg p-2 text-xs text-slate-500 italic">
            Descrição gerada com base nas métricas
          </div>
        )}
      </div>
    </div>
  );
};

const PriorityEditor = ({ priorityId, config, language, onUpdate }) => {
  const prioNum = priorityId.replace('priority_', '');
  const textField = `text_${language}`;
  const currentMode = config?.mode || 'auto';
  const currentText = config?.[textField] || '';
  const isLocked = config?.is_locked || false;

  return (
    <div className="border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 bg-[#C5A059] text-[#0A192F] rounded-full flex items-center justify-center text-xs font-bold">
            {prioNum}
          </span>
          <span className="font-medium text-slate-700">Prioridade {prioNum}</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={currentMode}
            onChange={(e) => onUpdate('mode', e.target.value)}
            className="text-xs border border-slate-200 rounded px-2 py-1"
          >
            <option value="auto">🤖 Auto</option>
            <option value="manual">✏️ Manual</option>
          </select>
          <button
            onClick={() => onUpdate('is_locked', !isLocked)}
            className={`p-1.5 rounded ${isLocked ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}
          >
            {isLocked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      
      <input
        type="text"
        value={currentText}
        onChange={(e) => onUpdate(textField, e.target.value)}
        placeholder={language === 'pt' ? 'Texto da prioridade...' : 'Priority text...'}
        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
      />
    </div>
  );
};

const PreviewSlide = ({ slideNum, title, data, language }) => {
  return (
    <div className="bg-[#0A192F] rounded-lg p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs bg-[#C5A059] text-[#0A192F] px-2 py-0.5 rounded font-bold">
          Slide {slideNum}
        </span>
        <span className="text-xs text-white">{title}</span>
      </div>
      
      {data.editorial_phrase && (
        <div className="bg-[#F7F6F2] rounded p-2 mb-2">
          <p className="text-xs text-slate-600 italic">{data.editorial_phrase}</p>
        </div>
      )}
      
      {data.message_cards && (
        <div className="grid grid-cols-3 gap-1">
          {data.message_cards.map((card, idx) => (
            <div key={idx} className="bg-white rounded p-2">
              <p className="text-[10px] font-bold text-[#0A192F]">{card.title}</p>
              <p className="text-[9px] text-slate-500 mt-1 line-clamp-2">{card.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PreviewSlide5 = ({ data, language }) => {
  return (
    <div className="bg-[#0A192F] rounded-lg p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs bg-[#C5A059] text-[#0A192F] px-2 py-0.5 rounded font-bold">
          Slide 5
        </span>
        <span className="text-xs text-white">
          {language === 'pt' ? 'Resumo Executivo' : 'Executive Summary'}
        </span>
      </div>
      
      {/* Conclusions */}
      {data.conclusions && (
        <div className="grid grid-cols-3 gap-1 mb-2">
          {data.conclusions.map((conc, idx) => (
            <div key={idx} className="bg-[#F7F6F2] rounded p-2">
              <p className="text-[10px] font-bold text-[#0A192F]">{conc.title}</p>
              <p className="text-[9px] text-slate-500 mt-1 line-clamp-2">{conc.desc}</p>
              <p className="text-[8px] text-emerald-600 mt-1 font-medium">{conc.highlight}</p>
            </div>
          ))}
        </div>
      )}
      
      {/* Priorities */}
      {data.priorities && (
        <div className="flex gap-1 mb-2">
          {data.priorities.map((prio, idx) => (
            <div key={idx} className="flex-1 bg-[#1E3A5F] rounded px-2 py-1">
              <p className="text-[8px] text-white line-clamp-2">{prio}</p>
            </div>
          ))}
        </div>
      )}
      
      {/* Closing */}
      {data.closing_phrase && (
        <div className="bg-[#1E3A5F] rounded p-2">
          <p className="text-[9px] text-white italic">{data.closing_phrase}</p>
        </div>
      )}
    </div>
  );
};

const AdminPage = () => {
  const [dataStatus, setDataStatus] = useState(null);
  const [recentUploads, setRecentUploads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadingType, setUploadingType] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedDataType, setSelectedDataType] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('data'); // 'data' or 'documents'
  const [supportDocuments, setSupportDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [docDescription, setDocDescription] = useState('');
  const [showDocPreview, setShowDocPreview] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);
  
  // Executive Messages State
  const [messagesConfig, setMessagesConfig] = useState(null);
  const [selectedReportType, setSelectedReportType] = useState('performance_financeira');
  const [selectedSlide, setSelectedSlide] = useState('slide_2');
  const [messageLang, setMessageLang] = useState('pt');
  const [messagesPreview, setMessagesPreview] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [savingMessages, setSavingMessages] = useState(false);
  const [editedFields, setEditedFields] = useState({});

  // Fetch data status
  const fetchDataStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/data/status`);
      setDataStatus(response.data.data_status);
      setRecentUploads(response.data.recent_uploads || []);
    } catch (error) {
      console.error('Error fetching data status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch support documents
  const fetchSupportDocuments = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/data/documents`);
      setSupportDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };
  
  // Fetch executive messages configuration
  const fetchMessagesConfig = async (reportType = selectedReportType) => {
    setLoadingMessages(true);
    try {
      const adminToken = localStorage.getItem('abc_admin_token') || 'abc-admin-2026-secure-token';
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/executive-messages/${reportType}`,
        { headers: { 'X-Admin-Token': adminToken } }
      );
      setMessagesConfig(response.data);
      setEditedFields({});
    } catch (error) {
      console.error('Error fetching messages config:', error);
      // Use default structure if API fails
      setMessagesConfig(getDefaultMessagesConfig(reportType));
    } finally {
      setLoadingMessages(false);
    }
  };
  
  // Preview resolved messages
  const fetchMessagesPreview = async () => {
    try {
      const adminToken = localStorage.getItem('abc_admin_token') || 'abc-admin-2026-secure-token';
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/executive-messages/${selectedReportType}/preview?language=${messageLang}`,
        { headers: { 'X-Admin-Token': adminToken } }
      );
      setMessagesPreview(response.data);
    } catch (error) {
      console.error('Error fetching messages preview:', error);
    }
  };
  
  // Save messages configuration
  const saveMessagesConfig = async () => {
    if (!messagesConfig || Object.keys(editedFields).length === 0) return;
    
    setSavingMessages(true);
    try {
      const adminToken = localStorage.getItem('abc_admin_token') || 'abc-admin-2026-secure-token';
      await axios.put(
        `${BACKEND_URL}/api/admin/executive-messages/${selectedReportType}`,
        { slides: messagesConfig.slides },
        { headers: { 'X-Admin-Token': adminToken } }
      );
      setEditedFields({});
      alert('Configuração guardada com sucesso!');
      fetchMessagesPreview();
    } catch (error) {
      console.error('Error saving messages config:', error);
      alert('Erro ao guardar configuração');
    } finally {
      setSavingMessages(false);
    }
  };
  
  // Reset messages to defaults
  const resetMessagesConfig = async () => {
    if (!window.confirm('Tem certeza que deseja repor as mensagens padrão? Isto irá eliminar todas as alterações manuais.')) {
      return;
    }
    
    try {
      const adminToken = localStorage.getItem('abc_admin_token') || 'abc-admin-2026-secure-token';
      await axios.post(
        `${BACKEND_URL}/api/admin/executive-messages/${selectedReportType}/reset?confirm=true`,
        {},
        { headers: { 'X-Admin-Token': adminToken } }
      );
      await fetchMessagesConfig();
      setMessagesPreview(null);
      alert('Configuração reposta para valores padrão');
    } catch (error) {
      console.error('Error resetting config:', error);
      alert('Erro ao repor configuração');
    }
  };
  
  // Default messages config structure
  const getDefaultMessagesConfig = (reportType) => ({
    report_type: reportType,
    slides: {
      slide_2: {
        name_pt: 'Formação do Resultado',
        name_en: 'Result Formation',
        editorial_phrase: { mode: 'auto', manual_text_pt: null, manual_text_en: null, is_locked: false },
        message_cards: {
          card_1: { mode: 'auto', title_pt: 'Volume: motor do crescimento', title_en: 'Volume: growth driver', text_pt: null, text_en: null, is_locked: false },
          card_2: { mode: 'auto', title_pt: 'Preço: principal pressão', title_en: 'Price: main pressure', text_pt: null, text_en: null, is_locked: false },
          card_3: { mode: 'auto', title_pt: 'Execução acima do plano', title_en: 'Execution above plan', text_pt: null, text_en: null, is_locked: false }
        }
      },
      slide_3: {
        name_pt: 'Formação da Margem',
        name_en: 'Margin Formation',
        editorial_phrase: { mode: 'auto', manual_text_pt: null, manual_text_en: null, is_locked: false },
        message_cards: {
          card_1: { mode: 'auto', title_pt: 'CMVMC: principal pressão', title_en: 'COGS: main pressure', text_pt: null, text_en: null, is_locked: false },
          card_2: { mode: 'auto', title_pt: 'Pessoal e G&A: disciplina', title_en: 'Personnel & G&A: discipline', text_pt: null, text_en: null, is_locked: false },
          card_3: { mode: 'auto', title_pt: 'Escala: motor da margem', title_en: 'Scale: margin driver', text_pt: null, text_en: null, is_locked: false }
        }
      },
      slide_4: {
        name_pt: 'Conversão em Caixa',
        name_en: 'Cash Conversion',
        scorecard_metrics: { mode: 'auto', is_locked: false },
        risk_triggers: { mode: 'auto', manual_triggers_pt: null, manual_triggers_en: null, is_locked: false }
      },
      slide_5: {
        name_pt: 'Resumo Executivo',
        name_en: 'Executive Summary',
        conclusions: {
          conclusion_1: { mode: 'auto', title_pt: 'Crescimento Sustentado', title_en: 'Sustained Growth', desc_pt: null, desc_en: null, is_locked: false },
          conclusion_2: { mode: 'auto', title_pt: 'Margem em Expansão', title_en: 'Margin Expansion', desc_pt: null, desc_en: null, is_locked: false },
          conclusion_3: { mode: 'auto', title_pt: 'Conversão Robusta', title_en: 'Robust Conversion', desc_pt: null, desc_en: null, is_locked: false }
        },
        priorities: {
          priority_1: { mode: 'auto', text_pt: 'Proteger preço médio e optimizar mix comercial', text_en: 'Protect average price and optimize commercial mix', is_locked: false },
          priority_2: { mode: 'auto', text_pt: 'Manter disciplina de custos e capturar escala', text_en: 'Maintain cost discipline and capture scale', is_locked: false },
          priority_3: { mode: 'auto', text_pt: 'Assegurar execução operacional e conversão em caixa', text_en: 'Ensure operational execution and cash conversion', is_locked: false }
        },
        closing_phrase: { mode: 'auto', manual_text_pt: null, manual_text_en: null, is_locked: false }
      }
    }
  });
  
  // Update field in local state
  const updateMessageField = (slideId, fieldPath, key, value) => {
    const newConfig = { ...messagesConfig };
    const pathParts = fieldPath.split('.');
    
    let target = newConfig.slides[slideId];
    for (let i = 0; i < pathParts.length - 1; i++) {
      target = target[pathParts[i]];
    }
    target[pathParts[pathParts.length - 1]][key] = value;
    
    setMessagesConfig(newConfig);
    setEditedFields(prev => ({
      ...prev,
      [`${slideId}.${fieldPath}.${key}`]: true
    }));
  };

  useEffect(() => {
    fetchDataStatus();
    fetchSupportDocuments();
  }, []);
  
  // Load messages config when tab changes to messages
  useEffect(() => {
    if (activeTab === 'messages' && !messagesConfig) {
      fetchMessagesConfig();
    }
  }, [activeTab]);
  
  // Reload messages when report type changes
  useEffect(() => {
    if (activeTab === 'messages') {
      fetchMessagesConfig(selectedReportType);
      setMessagesPreview(null);
    }
  }, [selectedReportType]);

  // Handle file upload
  const handleFileUpload = async (event, dataType) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingType(dataType);
    setUploadProgress(prev => ({ ...prev, [dataType]: 0 }));

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/data/upload/${dataType}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [dataType]: progress }));
          }
        }
      );

      if (response.data.success) {
        // Refresh data status
        await fetchDataStatus();
        alert(`Importados ${response.data.records_imported} registos com sucesso!`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail?.message || error.response?.data?.detail || 'Erro ao importar ficheiro';
      alert(errorMsg);
    } finally {
      setUploadingType(null);
      setUploadProgress(prev => ({ ...prev, [dataType]: 0 }));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Download template
  const downloadTemplate = async (templateId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/data/templates/${templateId}/download`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `template_${templateId}_abc.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download error:', error);
      alert('Erro ao descarregar template');
    }
  };

  // Clear data for a type
  const clearData = async (dataType) => {
    if (!window.confirm(`Tem certeza que deseja apagar todos os dados de ${DATA_TYPES[dataType].name}?`)) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/data/clear/${dataType}`);
      await fetchDataStatus();
      alert('Dados apagados com sucesso');
    } catch (error) {
      console.error('Clear error:', error);
      alert('Erro ao apagar dados');
    }
  };

  // Preview data
  const previewImportedData = async (dataType) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/data/imported/${dataType}`);
      setPreviewData(response.data);
      setSelectedDataType(dataType);
      setShowPreview(true);
    } catch (error) {
      console.error('Preview error:', error);
    }
  };

  // Handle document upload (Word/PDF)
  const handleDocumentUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingDoc(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', selectedCategory);
    formData.append('description', docDescription);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/data/documents/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        await fetchSupportDocuments();
        setDocDescription('');
        alert(`Documento carregado com sucesso! (${response.data.text_length} caracteres extraídos)`);
      }
    } catch (error) {
      console.error('Document upload error:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao carregar documento';
      alert(errorMsg);
    } finally {
      setUploadingDoc(false);
      if (docInputRef.current) {
        docInputRef.current.value = '';
      }
    }
  };

  // Delete document
  const deleteDocument = async (docId) => {
    if (!window.confirm('Tem certeza que deseja eliminar este documento?')) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/data/documents/${docId}`);
      await fetchSupportDocuments();
      alert('Documento eliminado');
    } catch (error) {
      console.error('Delete error:', error);
      alert('Erro ao eliminar documento');
    }
  };

  // Preview document
  const previewDocumentContent = async (docId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/data/documents/${docId}`);
      setPreviewDocument(response.data);
      setShowDocPreview(true);
    } catch (error) {
      console.error('Preview error:', error);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    if (status === 'imported') return 'bg-emerald-100 text-emerald-700';
    return 'bg-slate-100 text-slate-500';
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
          <span className="text-slate-600">A carregar...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm">Voltar ao Dashboard</span>
              </Link>
              <div className="w-px h-6 bg-slate-600" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                  <Settings className="w-5 h-5 text-[#C5A059]" />
                </div>
                <div>
                  <h1 className="text-xl font-serif font-semibold">Administração</h1>
                  <p className="text-xs text-slate-400">Gestão de Dados do Sistema</p>
                </div>
              </div>
            </div>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchDataStatus}
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Database className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-slate-800">
                    {dataStatus ? Object.values(dataStatus).filter(s => s.status === 'imported').length : 0}
                  </p>
                  <p className="text-xs text-slate-500">Tipos com Dados</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <HardDrive className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-slate-800">
                    {dataStatus ? Object.values(dataStatus).reduce((sum, s) => sum + (s.records || 0), 0) : 0}
                  </p>
                  <p className="text-xs text-slate-500">Registos Totais</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Upload className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-slate-800">
                    {recentUploads.length}
                  </p>
                  <p className="text-xs text-slate-500">Uploads Recentes</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-slate-800">
                    {recentUploads[0] ? formatDate(recentUploads[0].upload_date).split(',')[0] : '-'}
                  </p>
                  <p className="text-xs text-slate-500">Última Actualização</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('data')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              activeTab === 'data'
                ? 'bg-[#0A192F] text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Database className="w-4 h-4 inline mr-2" />
            Dados Estruturados
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              activeTab === 'documents'
                ? 'bg-[#0A192F] text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <FolderOpen className="w-4 h-4 inline mr-2" />
            Documentos de Suporte
            {supportDocuments.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                {supportDocuments.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            data-testid="tab-executive-messages"
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              activeTab === 'messages'
                ? 'bg-[#0A192F] text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <MessageSquare className="w-4 h-4 inline mr-2" />
            Mensagens Executivas
          </button>
        </div>

        {/* Data Tab Content */}
        {activeTab === 'data' && (
        <>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Data Types Grid */}
          <div className="lg:col-span-2">
            <Card className="bg-white border-0 shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-lg font-semibold text-slate-800">
                  Importação de Dados
                </CardTitle>
                <CardDescription>
                  Carregue ficheiros Excel para alimentar o sistema. Descarregue os templates para garantir o formato correcto.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(DATA_TYPES).map(([key, config]) => {
                    const status = dataStatus?.[key] || { status: 'empty', records: 0 };
                    const isUploading = uploadingType === key;
                    
                    return (
                      <div 
                        key={key}
                        className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{config.icon}</span>
                            <div>
                              <h3 className="font-semibold text-slate-800">{config.name}</h3>
                              <p className="text-xs text-slate-500">{config.description}</p>
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(status.status)}`}>
                            {status.status === 'imported' ? `${status.records} reg.` : 'Vazio'}
                          </span>
                        </div>

                        {status.status === 'imported' && (
                          <div className="text-xs text-slate-500 mb-3">
                            Anos: {status.min_year} - {status.max_year}
                          </div>
                        )}

                        <div className="flex gap-2">
                          {/* Download Template */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadTemplate(key)}
                            className="flex-1 text-xs"
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Template
                          </Button>

                          {/* Upload File */}
                          <div className="flex-1">
                            <input
                              type="file"
                              accept=".xlsx,.xls"
                              onChange={(e) => handleFileUpload(e, key)}
                              className="hidden"
                              id={`upload-${key}`}
                              disabled={isUploading}
                            />
                            <label htmlFor={`upload-${key}`}>
                              <Button
                                variant="default"
                                size="sm"
                                className="w-full text-xs cursor-pointer"
                                disabled={isUploading}
                                asChild
                              >
                                <span>
                                  {isUploading ? (
                                    <>
                                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                      {uploadProgress[key]}%
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="w-3 h-3 mr-1" />
                                      Importar
                                    </>
                                  )}
                                </span>
                              </Button>
                            </label>
                          </div>

                          {/* Preview/Clear */}
                          {status.status === 'imported' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => previewImportedData(key)}
                                className="px-2"
                                title="Visualizar dados"
                              >
                                <Eye className="w-4 h-4 text-slate-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => clearData(key)}
                                className="px-2"
                                title="Apagar dados"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Uploads */}
          <div className="lg:col-span-1">
            <Card className="bg-white border-0 shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-lg font-semibold text-slate-800">
                  Histórico de Uploads
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  {recentUploads.length === 0 ? (
                    <div className="p-6 text-center text-slate-500">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">Nenhum upload realizado</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {recentUploads.map((upload, idx) => (
                        <div key={idx} className="p-4 hover:bg-slate-50">
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              upload.status === 'completed' 
                                ? 'bg-emerald-100' 
                                : upload.status === 'failed'
                                ? 'bg-red-100'
                                : 'bg-amber-100'
                            }`}>
                              {upload.status === 'completed' ? (
                                <CheckCircle className="w-4 h-4 text-emerald-600" />
                              ) : upload.status === 'failed' ? (
                                <XCircle className="w-4 h-4 text-red-600" />
                              ) : (
                                <AlertCircle className="w-4 h-4 text-amber-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800 truncate">
                                {upload.filename}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs px-1.5 py-0.5 bg-slate-100 rounded text-slate-600">
                                  {DATA_TYPES[upload.data_type]?.name || upload.data_type}
                                </span>
                                <span className="text-xs text-slate-400">
                                  {formatFileSize(upload.file_size)}
                                </span>
                              </div>
                              <p className="text-xs text-slate-400 mt-1">
                                {formatDate(upload.upload_date)}
                              </p>
                              {upload.records_imported > 0 && (
                                <p className="text-xs text-emerald-600 mt-1">
                                  {upload.records_imported} registos importados
                                </p>
                              )}
                              {upload.error_message && (
                                <p className="text-xs text-red-600 mt-1">
                                  {upload.error_message}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Instructions */}
        <Card className="bg-white border-0 shadow-sm mt-8">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg font-semibold text-slate-800">
              Instruções de Importação
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold">1</span>
                </div>
                <div>
                  <h4 className="font-medium text-slate-800 mb-1">Descarregar Template</h4>
                  <p className="text-sm text-slate-500">
                    Clique em "Template" para obter o ficheiro Excel com as colunas correctas para cada tipo de dados.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold">2</span>
                </div>
                <div>
                  <h4 className="font-medium text-slate-800 mb-1">Preencher Dados</h4>
                  <p className="text-sm text-slate-500">
                    Preencha o template com os dados. Os campos amarelos são obrigatórios. Valores em M USD.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold">3</span>
                </div>
                <div>
                  <h4 className="font-medium text-slate-800 mb-1">Importar Ficheiro</h4>
                  <p className="text-sm text-slate-500">
                    Clique em "Importar" e seleccione o ficheiro. Os dados serão validados e carregados no sistema.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        </>
        )}

        {/* Documents Tab Content */}
        {activeTab === 'documents' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Upload Section */}
            <div className="lg:col-span-2">
              <Card className="bg-white border-0 shadow-sm">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-lg font-semibold text-slate-800">
                    Carregar Documentos
                  </CardTitle>
                  <CardDescription>
                    Carregue ficheiros Word (.docx) ou PDF para adicionar contexto ao sistema e ao assistente AI.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  {/* Upload Form */}
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 mb-6">
                    <div className="text-center">
                      <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="font-medium text-slate-700 mb-2">
                        Arraste ficheiros ou clique para seleccionar
                      </h3>
                      <p className="text-sm text-slate-500 mb-4">
                        Formatos suportados: PDF, Word (.docx) - Máx. 20MB
                      </p>
                      
                      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-slate-600">Categoria:</label>
                          <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="text-sm border border-slate-200 rounded-lg px-3 py-1.5"
                          >
                            {Object.entries(DOC_CATEGORIES).map(([key, cat]) => (
                              <option key={key} value={key}>{cat.icon} {cat.name}</option>
                            ))}
                          </select>
                        </div>
                        
                        <input
                          type="text"
                          placeholder="Descrição (opcional)"
                          value={docDescription}
                          onChange={(e) => setDocDescription(e.target.value)}
                          className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 w-48"
                        />
                      </div>
                      
                      <input
                        ref={docInputRef}
                        type="file"
                        accept=".pdf,.docx,.doc"
                        onChange={handleDocumentUpload}
                        className="hidden"
                        id="doc-upload"
                        disabled={uploadingDoc}
                      />
                      <label htmlFor="doc-upload">
                        <Button
                          className="mt-4 cursor-pointer"
                          disabled={uploadingDoc}
                          asChild
                        >
                          <span>
                            {uploadingDoc ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                A processar...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Seleccionar Ficheiro
                              </>
                            )}
                          </span>
                        </Button>
                      </label>
                    </div>
                  </div>

                  {/* Documents List */}
                  <div>
                    <h4 className="font-medium text-slate-700 mb-4">
                      Documentos Carregados ({supportDocuments.length})
                    </h4>
                    
                    {supportDocuments.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm">Nenhum documento carregado</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {supportDocuments.map((doc) => (
                          <div 
                            key={doc.id}
                            className="flex items-center gap-4 p-4 border border-slate-200 rounded-lg hover:bg-slate-50"
                          >
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              doc.file_type === '.pdf' ? 'bg-red-100' : 'bg-blue-100'
                            }`}>
                              <FileText className={`w-5 h-5 ${
                                doc.file_type === '.pdf' ? 'text-red-600' : 'text-blue-600'
                              }`} />
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-800 truncate">{doc.filename}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs px-2 py-0.5 bg-slate-100 rounded-full text-slate-600">
                                  {DOC_CATEGORIES[doc.category]?.icon} {DOC_CATEGORIES[doc.category]?.name || doc.category}
                                </span>
                                <span className="text-xs text-slate-400">
                                  {formatFileSize(doc.file_size)}
                                </span>
                                <span className="text-xs text-slate-400">
                                  {doc.text_length?.toLocaleString()} chars
                                </span>
                              </div>
                              {doc.description && (
                                <p className="text-xs text-slate-500 mt-1 truncate">{doc.description}</p>
                              )}
                            </div>
                            
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => previewDocumentContent(doc.id)}
                                title="Visualizar conteúdo"
                              >
                                <Eye className="w-4 h-4 text-slate-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteDocument(doc.id)}
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Info Panel */}
            <div className="lg:col-span-1">
              <Card className="bg-white border-0 shadow-sm">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-lg font-semibold text-slate-800">
                    Informações
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-medium text-blue-800 mb-2">Para que servem?</h4>
                      <p className="text-sm text-blue-700">
                        Os documentos de suporte fornecem contexto adicional ao assistente AI, 
                        permitindo análises mais precisas e completas.
                      </p>
                    </div>
                    
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <h4 className="font-medium text-amber-800 mb-2">Formatos Suportados</h4>
                      <ul className="text-sm text-amber-700 space-y-1">
                        <li>• <strong>PDF</strong> - Relatórios, apresentações</li>
                        <li>• <strong>Word (.docx)</strong> - Memorandos, análises</li>
                      </ul>
                    </div>
                    
                    <div className="p-4 bg-emerald-50 rounded-lg">
                      <h4 className="font-medium text-emerald-800 mb-2">Categorias</h4>
                      <ul className="text-sm text-emerald-700 space-y-1">
                        {Object.entries(DOC_CATEGORIES).map(([key, cat]) => (
                          <li key={key}>• {cat.icon} {cat.name}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Executive Messages Tab Content */}
        {activeTab === 'messages' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Message Configuration */}
            <div className="lg:col-span-2">
              <Card className="bg-white border-0 shadow-sm">
                <CardHeader className="border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg font-semibold text-slate-800">
                        Mensagens Executivas dos Relatórios
                      </CardTitle>
                      <CardDescription>
                        Configure as narrativas e mensagens que aparecem nos relatórios PDF. Modo híbrido: geração automática com possibilidade de override manual.
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      {Object.keys(editedFields).length > 0 && (
                        <Button
                          onClick={saveMessagesConfig}
                          disabled={savingMessages}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="save-messages-btn"
                        >
                          {savingMessages ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Guardar
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={resetMessagesConfig}
                        className="text-slate-600"
                        data-testid="reset-messages-btn"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Repor
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  {/* Report Type Selector */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-slate-600">Relatório:</label>
                      <select
                        value={selectedReportType}
                        onChange={(e) => setSelectedReportType(e.target.value)}
                        className="text-sm border border-slate-200 rounded-lg px-3 py-2"
                        data-testid="report-type-select"
                      >
                        <option value="performance_financeira">Performance Financeira</option>
                        <option value="liquidez_fluxo_caixa">Liquidez e Fluxo de Caixa</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Languages className="w-4 h-4 text-slate-400" />
                      <select
                        value={messageLang}
                        onChange={(e) => setMessageLang(e.target.value)}
                        className="text-sm border border-slate-200 rounded-lg px-3 py-2"
                        data-testid="language-select"
                      >
                        <option value="pt">Português</option>
                        <option value="en">English</option>
                      </select>
                    </div>
                  </div>

                  {loadingMessages ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                      <span className="ml-2 text-slate-500">A carregar configuração...</span>
                    </div>
                  ) : messagesConfig ? (
                    <>
                      {/* Slide Tabs */}
                      <div className="flex gap-2 mb-6 border-b border-slate-200 pb-3">
                        {Object.entries(messagesConfig.slides || {}).map(([slideId, slideData]) => (
                          <button
                            key={slideId}
                            onClick={() => setSelectedSlide(slideId)}
                            data-testid={`slide-tab-${slideId}`}
                            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                              selectedSlide === slideId
                                ? 'bg-[#0A192F] text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            {slideId.replace('slide_', 'Slide ')}
                            <span className="ml-2 text-xs opacity-75">
                              {slideData[`name_${messageLang}`] || slideData.name_pt}
                            </span>
                          </button>
                        ))}
                      </div>

                      {/* Slide Content Editor */}
                      {messagesConfig.slides[selectedSlide] && (
                        <div className="space-y-6">
                          {/* Editorial Phrase */}
                          {messagesConfig.slides[selectedSlide].editorial_phrase && (
                            <MessageFieldEditor
                              label="Frase Editorial"
                              description="Frase em itálico que contextualiza o slide"
                              fieldConfig={messagesConfig.slides[selectedSlide].editorial_phrase}
                              language={messageLang}
                              onUpdate={(key, value) => updateMessageField(selectedSlide, 'editorial_phrase', key, value)}
                              textKey="manual_text"
                            />
                          )}

                          {/* Message Cards */}
                          {messagesConfig.slides[selectedSlide].message_cards && (
                            <div>
                              <h4 className="text-sm font-semibold text-slate-700 mb-3">Cartões de Mensagem</h4>
                              <div className="space-y-4">
                                {Object.entries(messagesConfig.slides[selectedSlide].message_cards).map(([cardId, cardConfig]) => (
                                  <MessageCardEditor
                                    key={cardId}
                                    cardId={cardId}
                                    config={cardConfig}
                                    language={messageLang}
                                    onUpdate={(key, value) => updateMessageField(selectedSlide, `message_cards.${cardId}`, key, value)}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Conclusions (Slide 5) */}
                          {messagesConfig.slides[selectedSlide].conclusions && (
                            <div>
                              <h4 className="text-sm font-semibold text-slate-700 mb-3">Conclusões Principais</h4>
                              <div className="space-y-4">
                                {Object.entries(messagesConfig.slides[selectedSlide].conclusions).map(([concId, concConfig]) => (
                                  <ConclusionEditor
                                    key={concId}
                                    conclusionId={concId}
                                    config={concConfig}
                                    language={messageLang}
                                    onUpdate={(key, value) => updateMessageField(selectedSlide, `conclusions.${concId}`, key, value)}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Priorities (Slide 5) */}
                          {messagesConfig.slides[selectedSlide].priorities && (
                            <div>
                              <h4 className="text-sm font-semibold text-slate-700 mb-3">Prioridades de Gestão</h4>
                              <div className="space-y-4">
                                {Object.entries(messagesConfig.slides[selectedSlide].priorities).map(([prioId, prioConfig]) => (
                                  <PriorityEditor
                                    key={prioId}
                                    priorityId={prioId}
                                    config={prioConfig}
                                    language={messageLang}
                                    onUpdate={(key, value) => updateMessageField(selectedSlide, `priorities.${prioId}`, key, value)}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Closing Phrase (Slide 5) */}
                          {messagesConfig.slides[selectedSlide].closing_phrase && (
                            <MessageFieldEditor
                              label="Frase de Fecho"
                              description="Mensagem final do slide de resumo executivo"
                              fieldConfig={messagesConfig.slides[selectedSlide].closing_phrase}
                              language={messageLang}
                              onUpdate={(key, value) => updateMessageField(selectedSlide, 'closing_phrase', key, value)}
                              textKey="manual_text"
                            />
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Não foi possível carregar a configuração</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Preview Panel */}
            <div className="lg:col-span-1">
              <Card className="bg-white border-0 shadow-sm sticky top-6">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-lg font-semibold text-slate-800">
                    Pré-visualização
                  </CardTitle>
                  <CardDescription>
                    Veja como as mensagens aparecerão no PDF
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4">
                  <Button
                    onClick={fetchMessagesPreview}
                    className="w-full mb-4"
                    variant="outline"
                    data-testid="preview-messages-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Gerar Pré-visualização
                  </Button>

                  {messagesPreview ? (
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-4">
                        {/* Preview Slide 2 */}
                        {messagesPreview.resolved_messages.slide_2 && (
                          <PreviewSlide
                            slideNum="2"
                            title={messageLang === 'pt' ? 'Formação do Resultado' : 'Result Formation'}
                            data={messagesPreview.resolved_messages.slide_2}
                            language={messageLang}
                          />
                        )}
                        {/* Preview Slide 3 */}
                        {messagesPreview.resolved_messages.slide_3 && (
                          <PreviewSlide
                            slideNum="3"
                            title={messageLang === 'pt' ? 'Formação da Margem' : 'Margin Formation'}
                            data={messagesPreview.resolved_messages.slide_3}
                            language={messageLang}
                          />
                        )}
                        {/* Preview Slide 5 */}
                        {messagesPreview.resolved_messages.slide_5 && (
                          <PreviewSlide5
                            data={messagesPreview.resolved_messages.slide_5}
                            language={messageLang}
                          />
                        )}
                      </div>
                    </ScrollArea>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Sparkles className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">Clique para ver como as mensagens serão geradas</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>

      {/* Data Preview Modal */}
      {showPreview && previewData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-800">
                  Dados de {DATA_TYPES[selectedDataType]?.name}
                </h3>
                <p className="text-sm text-slate-500">
                  {previewData.count} registos
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPreview(false)}
              >
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
            <ScrollArea className="h-[60vh] p-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      {previewData.data[0] && Object.keys(previewData.data[0])
                        .filter(k => !k.startsWith('_'))
                        .map(key => (
                          <th key={key} className="text-left p-2 font-medium text-slate-600">
                            {key}
                          </th>
                        ))
                      }
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.data.map((row, idx) => (
                      <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                        {Object.entries(row)
                          .filter(([k]) => !k.startsWith('_'))
                          .map(([key, value], i) => (
                            <td key={i} className="p-2 text-slate-700">
                              {typeof value === 'number' 
                                ? value.toLocaleString('pt-PT', { maximumFractionDigits: 2 })
                                : String(value)
                              }
                            </td>
                          ))
                        }
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ScrollArea>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {showDocPreview && previewDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-800">
                  {previewDocument.filename}
                </h3>
                <p className="text-sm text-slate-500">
                  {DOC_CATEGORIES[previewDocument.category]?.icon} {DOC_CATEGORIES[previewDocument.category]?.name} • 
                  {previewDocument.text_length?.toLocaleString()} caracteres extraídos
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDocPreview(false)}
              >
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
            <ScrollArea className="h-[60vh] p-6">
              <pre className="whitespace-pre-wrap text-sm text-slate-700 font-mono leading-relaxed">
                {previewDocument.extracted_text}
              </pre>
            </ScrollArea>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
